import { useState, useEffect } from 'react';

const formatPathSegment = (segment: string) => {
  if (!segment) return '';
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface Breadcrumb {
  label: string;
  href?: string;
}

// See https://developers.google.com/search/docs/appearance/structured-data/breadcrumb#structured-data-type-definitions
interface SchemaListItem {
  '@type': 'ListItem';
  position: number;
  name: string;
  item?: string;
}

interface BreadcrumbSchema {
  '@type': 'BreadcrumbList';
  itemListElement: Array<SchemaListItem>;
}

interface UseBreadcrumbsProps {
  pageTitle?: string;
  breadcrumbItems?: Array<Breadcrumb>;
}

const generateSchemaListItem = (breadcrumb: Breadcrumb, index: number): SchemaListItem => {
  return {
    '@type': 'ListItem',
    position: index + 1,
    name: breadcrumb.label,
    ...(breadcrumb.href && { item: breadcrumb.href }),
  };
};

/** The crumbs to render plus the JSON-LD schema that mirrors them. */
interface UseBreadcrumbsResult {
  crumbs: Array<Breadcrumb>;
  schema: BreadcrumbSchema;
}

const useBreadcrumbs = ({ pageTitle, breadcrumbItems = [] }: UseBreadcrumbsProps): UseBreadcrumbsResult => {
  // If breadcrumbItems are provided, use them instead of generating breadcrumbs on the client
  const hasBreadcrumbItems = breadcrumbItems.length > 0;

  const initialSchema: BreadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: hasBreadcrumbItems
      ? breadcrumbItems.map((item, index) => generateSchemaListItem(item, index))
      : [],
  };

  const [generatedBreadcrumbs, setGeneratedBreadcrumbs] = useState<Array<{ label: string; href?: string }>>([]);
  const [schema, setSchema] = useState<BreadcrumbSchema>(initialSchema);

  useEffect(() => {
    if (typeof window !== 'undefined' && !hasBreadcrumbItems) {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);

      // Create home breadcrumb
      const homeBreadcrumb = { label: 'Home', href: '/' };

      const pathBreadcrumbs = pathSegments.map((segment, index) => {
        const isLast = index === pathSegments.length - 1;

        let href: string | undefined;
        if (!isLast) {
          href = `/${pathSegments.slice(0, index + 1).join('/')}`;
        }

        return {
          label: formatPathSegment(segment),
          href,
        };
      });

      // eslint-disable-next-line react-hooks/set-state-in-effect -- requires window.location which is only available in browser
      setGeneratedBreadcrumbs([homeBreadcrumb, ...pathBreadcrumbs]);
    }
  }, [hasBreadcrumbItems]);

  useEffect(() => {
    if (generatedBreadcrumbs.length > 0 && !hasBreadcrumbItems) {
      const lastItem = generatedBreadcrumbs[generatedBreadcrumbs.length - 1];
      const title = pageTitle || (typeof document !== 'undefined' ? document.title.replace('| Chime', '').trim() : '');
      if (title && lastItem && lastItem.label !== title) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- derives breadcrumb label from document.title
        setGeneratedBreadcrumbs(prev => {
          const currentLastItem = prev[prev.length - 1];
          return [...prev.slice(0, -1), { ...currentLastItem, label: title }];
        });
      }

      const newSchema: BreadcrumbSchema = {
        '@type': 'BreadcrumbList',
        itemListElement: generatedBreadcrumbs.map((breadcrumb, index) => generateSchemaListItem(breadcrumb, index)),
      };
      setSchema(newSchema);
    }
  }, [generatedBreadcrumbs, pageTitle, hasBreadcrumbItems]);

  return { crumbs: hasBreadcrumbItems ? breadcrumbItems : generatedBreadcrumbs, schema };
};

export default useBreadcrumbs;
